import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  BookOpen, Users, Globe, CheckCircle, Mail, ArrowRight,
  Upload, ScanLine, FileCheck, Combine, LayoutTemplate, Sparkles
} from 'lucide-react';
import ContactForm from '../components/ContactForm';

const Books: React.FC = () => {
  const services = [
    {
      title: 'Consulting & Guidance',
      description: 'Expert advice on book concept development, market positioning, and publishing strategy.',
      features: [
        'Book concept development',
        'Market research and positioning',
        'Publishing strategy consultation',
        'Author guidance and support'
      ],
      icon: Users
    },
    {
      title: 'Publishing Services',
      description: 'Complete publishing solutions from manuscript to finished book.',
      features: [
        'Professional editing and proofreading',
        'Book design and layout',
        'Cover design and branding',
        'Quality assurance and review'
      ],
      icon: BookOpen
    },
    {
      title: 'Distribution Channels',
      description: 'Comprehensive distribution network to reach your target audience.',
      features: [
        'Online retail platforms',
        'Traditional bookstore distribution',
        'Library and institutional sales',
        'International market access'
      ],
      icon: Globe
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Books & Publishing - Woodlogos</title>
        <meta name="description" content="Comprehensive publishing services from Woodlogos: consulting, editing, design, and distribution to bring your book from concept to finished work." />
        <link rel="canonical" href="https://www.woodlogos.net/books" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-blue-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center fade-in">
            <div className="w-20 h-20 bg-green-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <BookOpen className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Books & Publishing
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your ideas into published works with our comprehensive
              publishing services. From concept to distribution, we guide you
              through every step of the publishing journey.
            </p>

            {/* Studio 入口：作者自助上传书稿的主 CTA */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/author"
                className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors inline-flex items-center shadow-lg shadow-green-600/20"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Enter Author Studio
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="#studio"
                className="text-green-700 hover:text-green-800 font-medium inline-flex items-center"
              >
                See how it works
              </a>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              Upload your manuscript, review AI editorial suggestions, and track progress — no password needed.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Publishing Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive solutions tailored to meet your publishing needs
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={service.title}
                className={`bg-white rounded-2xl p-8 shadow-lg card-hover ${
                  index === 0 ? 'slide-in-left' : 
                  index === 1 ? 'fade-in' : 'slide-in-right'
                }`}
              >
                <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 text-lg">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Publishing Process
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A structured approach to ensure your book reaches its full potential
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Consultation',
                description: 'Initial discussion about your book concept, goals, and target audience.'
              },
              {
                step: '02',
                title: 'Development',
                description: 'Collaborative work on manuscript development, editing, and refinement.'
              },
              {
                step: '03',
                title: 'Production',
                description: 'Professional design, layout, and quality assurance processes.'
              },
              {
                step: '04',
                title: 'Distribution',
                description: 'Strategic distribution across multiple channels and markets.'
              }
            ].map((step, index) => (
              <div 
                key={step.step}
                className={`text-center fade-in`}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-lg">{step.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Author Studio — 自助书稿工作台入口 */}
      <section id="studio" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              <Sparkles className="h-4 w-4" />
              Author Studio
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Submit your manuscript online
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Upload a .docx and watch it move through editing and production.
              You review every suggested change — nothing is altered without your approval.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-14">
            {[
              { icon: Upload, title: 'Upload', description: 'Send us your .docx manuscript and confirm your rights to the work.' },
              { icon: ScanLine, title: 'Chapter split', description: 'We detect heading styles and split the manuscript into chapters.' },
              { icon: FileCheck, title: 'Editorial review', description: 'AI proposes fixes chapter by chapter. You accept, edit, or ignore each one.' },
              { icon: Combine, title: 'Proofread & merge', description: 'Accepted edits are applied, proofread, and merged back into one file.' },
              { icon: LayoutTemplate, title: 'Production', description: 'Layout, ISBN, copyright page, print-ready PDF, and cover design.' },
            ].map((step, index) => (
              <div key={step.title} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                  <step.icon className="h-6 w-6 text-green-600" />
                </div>
                <div className="text-xs font-semibold text-green-600 mb-1">STEP {index + 1}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 md:p-12 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Ready when you are</h3>
            <p className="text-gray-700 max-w-xl mx-auto mb-8">
              Sign in with your email — we'll send you a code, no password to remember.
              Your manuscripts stay on your shelf so you can pick up where you left off.
            </p>
            <Link
              to="/author"
              className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors inline-flex items-center"
            >
              <BookOpen className="mr-2 h-5 w-5" />
              Enter Author Studio
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose Woodlogos for Publishing?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                We combine traditional publishing expertise with modern digital 
                solutions to deliver exceptional results for our authors.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    title: 'Expert Guidance',
                    description: 'Professional consultation from concept to completion'
                  },
                  {
                    title: 'Quality Assurance',
                    description: 'Rigorous editing and quality control processes'
                  },
                  {
                    title: 'Modern Solutions',
                    description: 'Integration of digital tools and traditional publishing'
                  },
                  {
                    title: 'Global Reach',
                    description: 'Access to international distribution networks'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-gray-600">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="fade-in">
              <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Ready to Start Your Publishing Journey?
                </h3>
                <p className="text-gray-700 mb-8">
                  Contact our books team to discuss your project and learn 
                  how we can help bring your ideas to life.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center text-gray-700">
                    <Mail className="h-5 w-5 text-green-600 mr-3" />
                    <ContactForm 
                      inquiryType="books"
                      className="text-green-600 hover:text-green-700 font-medium"
                    >
                      Contact Books Team
                    </ContactForm>
                  </div>
                  <div className="text-sm text-gray-600">
                    We typically respond within 24 hours
                  </div>
                </div>
                <ContactForm 
                  inquiryType="books"
                  className="btn-primary inline-flex items-center mt-6"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Get Started Today
                </ContactForm>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Have a Book Idea? Let's Talk!
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
            Whether you're a first-time author or an experienced writer, 
            we're here to help you succeed in the publishing world.
          </p>
            <ContactForm 
              inquiryType="books"
              className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors inline-flex items-center"
            >
              <Mail className="mr-2 h-5 w-5" />
              Contact Our Books Team
            </ContactForm>
        </div>
      </section>
    </div>
  );
};

export default Books;
